import { useEffect, useRef, useState } from 'react'

export interface BidUpdateMessage {
  type: string
  auctionId?: string
  message: string
  timestamp: string
  bidData?: {
    bidId: string
    bidderId: string
    amount: number
    placedAt: string
    status: string
  }
  auctionData?: {
    auctionId: string
    currentPrice: number
    currentLeaderId?: string
    status: string
    endsAt: string
    totalBids: number
  }
}

interface UseBiddingWebSocketProps {
  auctionId?: string
  onBidUpdate?: (message: BidUpdateMessage) => void
  onConnectionChange?: (connected: boolean) => void
}

export function useBiddingWebSocket({
  auctionId,
  onBidUpdate,
  onConnectionChange
}: UseBiddingWebSocketProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<BidUpdateMessage | null>(null)
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)

  const maxReconnectAttempts = 5
  const reconnectDelay = 3000

  const connect = () => {
    try {
      // Determine WebSocket URL based on current environment
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/ws/bidding`

      console.log('Connecting to WebSocket:', wsUrl)
      
      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)
        reconnectAttemptsRef.current = 0
        onConnectionChange?.(true)

        // Subscribe to auction updates if auctionId is provided
        if (auctionId) {
          subscribeToAuction(auctionId)
        }
      }

      socket.onmessage = (event) => {
        try {
          const message: BidUpdateMessage = JSON.parse(event.data)
          console.log('WebSocket message received:', message)
          
          setLastMessage(message)
          onBidUpdate?.(message)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      socket.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      socket.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        setIsConnected(false)
        socketRef.current = null
        onConnectionChange?.(false)

        // Attempt to reconnect if not a manual close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          console.log(`Attempting to reconnect... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`)
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, reconnectDelay)
        }
      }

    } catch (error) {
      console.error('Error creating WebSocket connection:', error)
    }
  }

  const subscribeToAuction = (auctionId: string) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const message = {
        action: 'subscribe',
        auctionId: auctionId
      }
      socketRef.current.send(JSON.stringify(message))
      console.log('Subscribed to auction:', auctionId)
    }
  }

  const unsubscribeFromAuction = (auctionId: string) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const message = {
        action: 'unsubscribe',
        auctionId: auctionId
      }
      socketRef.current.send(JSON.stringify(message))
      console.log('Unsubscribed from auction:', auctionId)
    }
  }

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    
    if (socketRef.current) {
      socketRef.current.close(1000, 'Manual disconnect')
    }
  }

  // Connect on mount
  useEffect(() => {
    connect()

    return () => {
      disconnect()
    }
  }, [])

  // Subscribe/unsubscribe when auctionId changes
  useEffect(() => {
    if (isConnected && auctionId) {
      subscribeToAuction(auctionId)
      
      return () => {
        unsubscribeFromAuction(auctionId)
      }
    }
  }, [auctionId, isConnected])

  return {
    isConnected,
    lastMessage,
    subscribeToAuction,
    unsubscribeFromAuction,
    disconnect
  }
}